import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useConvexMutation } from '../use-convex-mutation';
import type { Id } from '../../../convex/_generated/dataModel';

export interface CreateSessionData {
  date: string;
  startTime: string;
  endTime: string;
  sessionType: string;
  courtNumber?: number;
}

type SessionType = "group" | "private" | "clinic" | "match_play" | "cardio_tennis" | "squads";

export function useTodaySessions(date?: string) {
  const dateParam = date || new Date().toISOString().split('T')[0];
  const data = useQuery(api.coach.sessions.list, { date: dateParam });
  return { data, isLoading: data === undefined, error: null };
}

export function useSessionsForMonth(year: number, month: number) {
  const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  const data = useQuery(api.coach.sessions.list, { startDate, endDate });
  return { data, isLoading: data === undefined, error: null };
}

export function useCreateSession() {
  const { mutateAsync, isPending } = useConvexMutation(api.coach.sessions.create);
  return {
    isPending,
    mutateAsync: (args: CreateSessionData) => mutateAsync({
      date: args.date,
      startTime: args.startTime,
      endTime: args.endTime,
      sessionType: args.sessionType as SessionType | undefined,
      courtNumber: args.courtNumber,
    }),
  };
}

export function useCoachDrillSuggestions() {
  const data = useQuery(api.coach.sessions.getSuggestedDrills);
  return data ?? [];
}

export function useUpdateDrillLog(sessionId: string) {
  const { mutateAsync, isPending } = useConvexMutation(api.coach.sessions.updateDrillLog);
  return {
    isPending,
    mutateAsync: (drills: string[]) =>
      mutateAsync({ sessionId: sessionId as Id<"trainingSessions">, drills }),
  };
}

export function useUpdateClinicTopic(sessionId: string) {
  const { mutateAsync, isPending } = useConvexMutation(api.coach.sessions.updateClinicTopic);
  return {
    isPending,
    mutateAsync: (topic: string) =>
      mutateAsync({ sessionId: sessionId as Id<"trainingSessions">, topic }),
  };
}

export function useUpdateIntensityLevel(sessionId: string) {
  const { mutateAsync, isPending } = useConvexMutation(api.coach.sessions.updateIntensityLevel);
  return {
    isPending,
    mutateAsync: (level: "low" | "medium" | "high" | null) =>
      mutateAsync({ sessionId: sessionId as Id<"trainingSessions">, level: level ?? undefined }),
  };
}
